import React, { useEffect, useRef } from 'react';
import { APP_STORE, getStoreUrl } from '../config/appStoreConfig';
import { getTargetPlatform } from '../utils/deviceDetection';
import { analytics } from '../analytics';

// Inline SVG badges modeled on the official Apple "Download on the App Store"
// and Google Play "Get it on Google Play" marks. Black-fill variant.
// Brand guidelines: https://developer.apple.com/app-store/marketing/guidelines/
//                   https://partnermarketinghub.withgoogle.com/brands/google-play/

const AppleBadge = ({ height = 44 }) => {
  const width = height * 3.0;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 120 40"
      width={width}
      height={height}
      role="img"
      aria-label="Download on the App Store"
    >
      <rect width="120" height="40" rx="6" fill="#000" />
      <rect x="0.5" y="0.5" width="119" height="39" rx="5.5" fill="none" stroke="#a6a6a6" strokeOpacity="0.4" />
      {/* Apple logo */}
      <path
        d="M21.7 20.4c0-2.1 1.7-3.1 1.8-3.2-1-1.4-2.5-1.6-3-1.6-1.3-.1-2.5.8-3.1.8-.6 0-1.6-.7-2.7-.7-1.4 0-2.7.8-3.4 2.1-1.5 2.5-.4 6.3 1 8.3.7 1 1.5 2.1 2.6 2.1 1 0 1.4-.7 2.7-.7 1.2 0 1.6.7 2.7.7 1.1 0 1.8-1 2.5-2 .8-1.2 1.1-2.3 1.1-2.4-.1 0-2.2-.8-2.2-3.4zm-2-6.3c.6-.7 1-1.7.9-2.6-.8 0-1.8.5-2.4 1.2-.5.6-1 1.6-.9 2.5.9.1 1.8-.4 2.4-1.1z"
        fill="#fff"
      />
      <text
        x="34"
        y="16"
        fill="#fff"
        fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif"
        fontSize="7"
        letterSpacing="0.2"
      >
        Download on the
      </text>
      <text
        x="34"
        y="30"
        fill="#fff"
        fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif"
        fontSize="15"
        fontWeight="600"
      >
        App Store
      </text>
    </svg>
  );
};

const GooglePlayBadge = ({ height = 44 }) => {
  const width = height * 3.0;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 135 40"
      width={width}
      height={height}
      role="img"
      aria-label="Get it on Google Play"
    >
      <rect width="135" height="40" rx="6" fill="#000" />
      <rect x="0.5" y="0.5" width="134" height="39" rx="5.5" fill="none" stroke="#a6a6a6" strokeOpacity="0.4" />
      <g transform="translate(11 9)">
        <path d="M0 0v22l11-11z" fill="#ea4335" opacity="0.95" />
        <path d="M0 0l11 11 3-3z" fill="#fbbc04" opacity="0.95" />
        <path d="M0 22l11-11 3 3z" fill="#34a853" opacity="0.95" />
        <path d="M11 11l3-3 4 3-4 3z" fill="#4285f4" opacity="0.95" />
      </g>
      <text
        x="34"
        y="16"
        fill="#fff"
        fontFamily="'Roboto', 'Segoe UI', Helvetica, Arial, sans-serif"
        fontSize="7"
        letterSpacing="0.7"
      >
        GET IT ON
      </text>
      <text
        x="34"
        y="31"
        fill="#fff"
        fontFamily="'Roboto', 'Segoe UI', Helvetica, Arial, sans-serif"
        fontSize="15"
        fontWeight="600"
      >
        Google Play
      </text>
    </svg>
  );
};

const AppStoreBadges = ({
  variant = 'auto',
  placement = 'unknown',
  height = 44,
  layout = 'row',
  className = '',
}) => {
  const platform = variant === 'auto' ? getTargetPlatform() : variant;
  const viewFiredRef = useRef(false);

  useEffect(() => {
    if (viewFiredRef.current) return;
    viewFiredRef.current = true;
    analytics.appDownloadCtaViewed(placement);
  }, [placement]);

  const handleClick = (storePlatform) => {
    analytics.appDownloadClicked(placement, storePlatform);
  };

  const showApple = platform === 'ios' || platform === 'both' || platform === 'desktop';
  const showGoogle = platform === 'android' || platform === 'both' || platform === 'desktop';

  const wrapperClasses = [
    'inline-flex',
    layout === 'column' ? 'flex-col' : 'flex-row flex-wrap',
    'items-center',
    'gap-3',
    className,
  ].join(' ');

  return (
    <div className={wrapperClasses}>
      {showApple && (
        <a
          href={getStoreUrl('ios', placement)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => handleClick('ios')}
          className="inline-block transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md"
          aria-label="Download Pickify on the App Store"
        >
          <AppleBadge height={height} />
        </a>
      )}
      {showGoogle && (
        <a
          href={getStoreUrl('android', placement)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => handleClick('android')}
          className="inline-block transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md"
          aria-label="Get Pickify on Google Play"
        >
          <GooglePlayBadge height={height} />
        </a>
      )}
    </div>
  );
};

export default AppStoreBadges;
export { APP_STORE };
